import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

// components
import CartUpsell from '../CartUpsell';
import CartUpsellMembershipModal from '@modals/CartUpsellMembershipModal';
import { DividerLightGreyStyled } from '@styled';

// functionality
import { formatMoney } from '@general/helpers/currency.helpers';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useEventFromAPI from '@hooks/apiLegacy/useEventFromAPI';
import useMemberFromAPI from '@hooks/apiLegacy/useMemberFromAPI';
import useRegistrantClassFromAPI from '@hooks/apiLegacy/useRegistrantClassFromAPI';
import useEventPrices from '@hooks/useEventPrices';
import useViewer from '@hooks/useViewer';
import useIsCartMultiCurrency from '@hooks/useIsCartMultiCurrency';
import MultiCurrencyCartModal from '@modals/MultiCurrencyCartModal';

const DividerStyled = styled(DividerLightGreyStyled)`
    margin-bottom: 14px;
    margin-top: 14px;
`;

const CartItemEventDiscountUpsell = ({ eventCode, memberId }) => {
    const [ctaText, setCtaText] = useState();
    const [isMultiCurrencyModalOpen, setIsMultiCurrencyModalOpen] = useState(false);
    const [isMembershipUpsellModalOpen, setIsMembershipUpsellModalOpen] = useState(false);
    const { viewer } = useViewer();
    const { cart } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { event: eventFromAPI } = useEventFromAPI({ code: eventCode });
    const { member } = useMemberFromAPI({ id: memberId });
    const { registrantClass } = useRegistrantClassFromAPI({
        memberId,
        cartId: cart?.id,
        eventId: eventFromAPI?.id,
    });
    const { isCartMultiCurrency } = useIsCartMultiCurrency({ locale: eventFromAPI?.locale });

    const { memberPrice, nonMemberPrice } = useEventPrices({ eventCode });

    useEffect(() => {
        if (!viewer || !member || !cart || !memberPrice || !nonMemberPrice) {
            return;
        }

        const isSelf = member.id === viewer?.recordId;
        const discount = nonMemberPrice - memberPrice;

        let textBuilder = '';

        if (isSelf) {
            textBuilder += 'Become a member';
        } else {
            textBuilder += `Add a membership for ${member.first_name}`;
        }

        textBuilder += ` and save ${formatMoney(discount, cart.locale)} off this event.`;
        setCtaText(textBuilder);
    }, [viewer, member, memberPrice, nonMemberPrice, cart]);

    if (!registrantClass || !registrantClass.includes('NM') || registrantClass.includes('SM') || !ctaText) {
        return null;
    }

    return (
        <div>
            <DividerStyled />
            {isMultiCurrencyModalOpen ? (
                <MultiCurrencyCartModal
                    isOpen={isMultiCurrencyModalOpen}
                    onClose={() => setIsMultiCurrencyModalOpen(false)}
                />
            ) : null}
            <CartUpsell
                text={ctaText}
                onClick={() => {
                    // Memberships are currently USD only, so never show membership upsell on non-US events
                    if (isCartMultiCurrency || eventFromAPI?.locale !== 'US') {
                        setIsMultiCurrencyModalOpen(true);
                        return;
                    }

                    setIsMultiCurrencyModalOpen(false);
                    setIsMembershipUpsellModalOpen(true);
                }}
            />
            <CartUpsellMembershipModal
                isOpen={isMembershipUpsellModalOpen}
                onClose={() => setIsMembershipUpsellModalOpen(false)}
                limitToMemberId={member.id}
            />
        </div>
    );
};

CartItemEventDiscountUpsell.propTypes = {
    eventCode: PropTypes.string.isRequired,
    memberId: PropTypes.number.isRequired,
};

export default CartItemEventDiscountUpsell;
